import { DotsIcon, SaveIcon } from '@approvalmax/ui';
import {
    Button,
    Checkbox,
    Dropdown,
    Flex,
    Form,
    Label,
    Menu,
    Select,
    Spacing,
    TextField,
} from '@approvalmax/ui/src/components';
import { useAtomValue, useSetAtom } from 'jotai';
import { domain } from 'modules/data';
import { useUserProfile } from 'modules/utils';
import { memo } from 'react';
import bemFactory from 'react-bem-factory';
import { useTimeZoneSelect } from 'shared/hooks/useTimeZoneSelect';

import { popupDeleteOrganisationState } from '../PopupDeleteOrganisation/PopupDeleteOrganisation.states';
import { popupTransferOrganisationState } from '../PopupTransferOrganisation/PopupTransferOrganisation.states';
import { netSuiteTaxCodeListsIncludeValues } from './OrganisationInfo.config';
import {
    useOrganisationCurrencies,
    useOrganisationFinancialYearEnd,
    useOrganisationInfoFormTwoFaEnforcement,
    useOrganisationLockDate,
    useOrganisationNetSuiteApiFilter,
} from './OrganisationInfo.hooks';
import { messages } from './OrganisationInfo.messages';
import { savingOrganisationState } from './OrganisationInfo.state';
import { OrganisationInfoProps } from './OrganisationInfo.types';

const qa = bemFactory.qa('cps-company-section');

export const OrganisationInfo = memo<OrganisationInfoProps>((props) => {
    const { company } = props;

    const me = useUserProfile();
    const isSavingOrganisation = useAtomValue(savingOrganisationState);

    const showDeletePopup = useSetAtom(popupDeleteOrganisationState);
    const showTransferPopup = useSetAtom(popupTransferOrganisationState);

    const { showLockDate } = useOrganisationLockDate(company);
    const { showFinancialYearEnd } = useOrganisationFinancialYearEnd(company);

    const { currencies } = useOrganisationCurrencies(company);
    const { enforcementOptions, enforcementIsLoading } = useOrganisationInfoFormTwoFaEnforcement(company);

    const isReadonlyControls = isSavingOrganisation || company.flags.isWorkflowManager;
    const isReadonlyCompany = isSavingOrganisation || company.flags.isWorkflowManager || company.isReadonly;

    const { isLoading, data: netSuiteApiFilter } = useOrganisationNetSuiteApiFilter();

    return (
        <Form.Part>
            <Flex spacing='16' direction='column' wrap data-qa={qa()}>
                <Flex block>
                    <TextField
                        name='name'
                        data-qa={qa('company-name-input')}
                        label={messages.organisationNameLabel}
                        placeholder={messages.organisationNamePlaceholder}
                        size='small'
                        clearable={false}
                        maxLength={400}
                        disabled={isReadonlyControls}
                        required
                    />
                </Flex>

                <Flex block>
                    <Select
                        name='enforcementTfaType'
                        data-qa={qa('enforcement-2fa-input')}
                        label={messages.enforcementTwoFaLabel}
                        size='small'
                        itemIdKey='id'
                        itemNameKey='text'
                        items={enforcementOptions}
                        clearable={false}
                        disabled={isReadonlyControls || enforcementOptions.length === 1 || enforcementIsLoading}
                    />
                </Flex>

                <Flex block>
                    <Select
                        name='timeZone'
                        data-qa={qa('timezone-dropdown')}
                        label={messages.timeZoneLabel}
                        placeholder={messages.timeZonePlaceholder}
                        size='small'
                        clearable={false}
                        disabled={
                            isReadonlyCompany || company?.integration?.integrationType === domain.IntegrationType.Xero
                        }
                        {...useTimeZoneSelect(company.id)}
                    />
                </Flex>

                <Flex spacing='16' container>
                    <Flex size={12}>
                        <Select
                            name='defaultCurrency'
                            data-qa={qa('currency-dropdown')}
                            label={messages.currencyFieldText}
                            placeholder={messages.currencyPlaceholder}
                            size='small'
                            itemIdKey='id'
                            itemNameKey='text'
                            items={currencies}
                            clearable={false}
                            disabled={isReadonlyCompany || Boolean(company.integration)}
                        />
                    </Flex>
                </Flex>

                {(showFinancialYearEnd || showLockDate) && (
                    <Flex spacing='16' container>
                        {showFinancialYearEnd && (
                            <Flex size={12}>
                                <TextField
                                    name='financialYearEnd'
                                    data-qa={qa('financial-year-end-editor')}
                                    label={messages.financialYearEndFieldText}
                                    size='small'
                                    disabled
                                />
                            </Flex>
                        )}

                        {showLockDate && (
                            <Flex size={12}>
                                <TextField
                                    name='lockDate'
                                    data-qa={qa('lock-date-editor')}
                                    label={messages.lockDateFieldText}
                                    size='small'
                                    disabled
                                />
                            </Flex>
                        )}
                    </Flex>
                )}

                {netSuiteApiFilter && (
                    <Flex block>
                        <Select
                            name='taxCodeListsInclude'
                            label={messages.taxCodeLists}
                            placeholder={messages.taxCodeListsPlaceholder}
                            size='small'
                            itemIdKey='id'
                            itemNameKey='text'
                            items={netSuiteTaxCodeListsIncludeValues}
                            clearable={false}
                            disabled={isReadonlyCompany || isLoading}
                        />
                    </Flex>
                )}

                {netSuiteApiFilter && (
                    <Flex block>
                        <Label size='small'>{messages.isAccountExpandedFieldText}</Label>

                        <Spacing height={2} />

                        <Checkbox name='isExpandAccount' size='small' disabled={isReadonlyCompany || isLoading}>
                            {messages.isAccountExpandedCheckboxText}
                        </Checkbox>
                    </Flex>
                )}
            </Flex>

            <Spacing height={24} />

            <Flex spacing='16' alignItems='center'>
                <Flex shrink={0} grow={0}>
                    <Dropdown
                        activator={
                            <Button icon noPadding data-qa={qa('action-menu')} variant='text' color='blue100'>
                                <DotsIcon size={24} />
                            </Button>
                        }
                        disabled={isReadonlyControls}
                        width={150}
                        placement='bottom-start'
                        floatingFocusManagerProps={{ returnFocus: false }}
                        closeOnClickInnerButton
                    >
                        <Menu divider size='small'>
                            {company.flags.isManager && company.author !== me.email && (
                                <Menu.Item
                                    data-qa={qa('transfer-company-menu-item')}
                                    title={messages.transferCompanyMenuItemTitle}
                                    name={messages.transferCompanyMenuItemText}
                                    onClick={() => showTransferPopup(true)}
                                />
                            )}

                            {company.flags.isAccountOwner && (
                                <Menu.Item
                                    data-qa={qa('delete-company-menu-item')}
                                    title={messages.deleteCompanyMenuItemTitle}
                                    name={messages.deleteCompanyMenuItemText}
                                    onClick={() => showDeletePopup(true)}
                                />
                            )}
                        </Menu>
                    </Dropdown>
                </Flex>

                <Flex shrink={0} grow={1}>
                    <Button
                        disabled={isReadonlyControls}
                        data-qa={qa('save-button')}
                        color='green80'
                        startIcon={<SaveIcon size={16} />}
                        size='medium'
                        type='submit'
                        block
                    >
                        {messages.buttonSaveChanges}
                    </Button>
                </Flex>
            </Flex>
        </Form.Part>
    );
});

OrganisationInfo.displayName = 'OrganisationInfo';
